import { create } from 'zustand';
import { io, Socket } from 'socket.io-client';
import { IGameState, Card, RoundResult, Table } from '../types/game';
import { toast } from 'react-toastify';
import { SOCKET_URL } from '../api/socket';
import { buildRoundDeltaMessage, getRoundAnnouncement } from '../utils/roundResults';

let presenceInterval: ReturnType<typeof setInterval> | null = null;
const DEBUG_GAME_SOCKET = process.env.REACT_APP_DEBUG_GAME_SOCKET === 'true';

interface GameStore {
  socket: Socket | null;
  gameState: IGameState | null;
  tableInfo: Table | null;
  lastRoundResult: RoundResult | null;
  isConnected: boolean;
  error: string | null;

  connect: (
    tableId: string,
    userId: string,
    username: string,
    avatarUrl?: string,
    contestId?: string,
    inviteCode?: string,
    spectator?: boolean
  ) => void;
  disconnect: () => void;
  
  // Actions
  leaveTable: (tableId: string, userId: string, username: string) => void;
  drawCard: (tableId: string, userId: string, source: 'deck' | 'discard') => void;
  discardCard: (tableId: string, userId: string, card: Card) => void;
  spread: (tableId: string, userId: string, cards: Card[]) => void;
  hit: (tableId: string, userId: string, card: Card, targetPlayerId: string, targetSpreadIndex: number) => void;
  drop: (tableId: string, userId: string) => void;
  declare41: (tableId: string, userId: string) => void;
  reorderHand: (tableId: string, userId: string, handOrder: string[]) => void;
  requestLeaveTable: (tableId: string, userId: string) => void;
  putIn: (tableId: string, userId: string) => void;
}

export const useGameStore = create<GameStore>((set, get) => ({
  socket: null,
  gameState: null,
  tableInfo: null,
  lastRoundResult: null,
  isConnected: false,
  error: null,

  connect: (tableId, userId, username, avatarUrl, contestId, inviteCode, spectator) => {
    const existingSocket = get().socket;
    if (existingSocket) {
        existingSocket.disconnect();
    }

    set({ gameState: null, tableInfo: null, lastRoundResult: null, error: null });

    if (presenceInterval) {
      clearInterval(presenceInterval);
      presenceInterval = null;
    }

    const socket = io(SOCKET_URL);
    let lastRoundEndTimestamp: number | null = null;
    let activeRoundEndToastId: string | number | null = null;

    socket.on('connect', () => {
      if (DEBUG_GAME_SOCKET) console.log('Connected to game server');
      set({ isConnected: true, error: null });
      if (DEBUG_GAME_SOCKET) console.log(`Emitting joinTable for ${username} (${userId}) in table ${tableId}`);
      socket.emit('joinTable', { tableId, userId, username, avatarUrl, contestId, inviteCode, spectator });
      socket.emit('presenceHeartbeat', { userId });
    });

    socket.on('initialGameState', (gameState: IGameState) => {
      if (DEBUG_GAME_SOCKET) console.log('Received initialGameState:', gameState);
      set({ gameState });
    });

    socket.on('gameStateUpdate', (gameState: IGameState) => {
      if (DEBUG_GAME_SOCKET) console.log('Received gameStateUpdate:', gameState);
      set({ gameState });
      
      // Check for round end and notify
      if (gameState.status === 'round-end') {
          const roundEndTimestamp = gameState.lastAction?.timestamp ?? Date.now();
          if (lastRoundEndTimestamp === roundEndTimestamp) {
            return;
          }
          lastRoundEndTimestamp = roundEndTimestamp;
          const announcement = getRoundAnnouncement(gameState);
          if (announcement) {
            if (gameState.roundEndedBy === 'REEM' || gameState.roundEndedBy === 'AUTO_TRIPLE') {
              toast.success(announcement);
            } else {
              toast.info(announcement);
            }
          }
          const deltaMessage = buildRoundDeltaMessage(gameState);
          if (deltaMessage) {
            toast.info(deltaMessage);
          }
          if (activeRoundEndToastId !== null) {
            toast.dismiss(activeRoundEndToastId);
          }
          const isContinuousMode = !gameState.mode || gameState.mode === 'FREE_RTC_TABLE';
          if (isContinuousMode) {
            activeRoundEndToastId = toast.info("Next hand starting...", { autoClose: 1000 });
          } else {
            activeRoundEndToastId = toast.info("Match complete. Return to the lobby to start another session.", {
              autoClose: 5000,
            });
          }
      } else {
          if (activeRoundEndToastId !== null) {
            toast.dismiss(activeRoundEndToastId);
            activeRoundEndToastId = null;
          }
          lastRoundEndTimestamp = null;
      }
    });

    socket.on('roundResult', (roundResult: RoundResult) => {
      if (DEBUG_GAME_SOCKET) console.log('Received roundResult:', roundResult);
      set({ lastRoundResult: roundResult });
    });
    
    socket.on('tableUpdate', (data: { message: string, table: Table, gameState?: IGameState }) => {
        if (data.message) toast.info(data.message);
        set((state) => ({
          tableInfo: data.table ?? state.tableInfo,
          gameState: data.gameState ?? state.gameState,
        }));
    });

    socket.on('gameError', (data: { message: string }) => {
      toast.error(data.message);
      set({ error: data.message });
    });

    socket.on("turnExpired", (data: { message?: string }) => {
      toast.info(data.message ?? "Turn expired.", {
        position: "top-center",
        autoClose: 2000,
      });
    });

    socket.on('ackLeaveRequest', () => {
      toast.info("You will be removed from the table at the end of the round.");
    });

    socket.on('playerLeft', ({ userId: leftPlayerId }) => {
      const { gameState } = get();
      if (gameState && gameState.players.some(p => p.userId === leftPlayerId)) {
        // You might want to handle redirection here if the current user is the one who left.
        // This can be done by checking against the current user's ID from useAuthStore.
        // For now, just logging it.
        if (DEBUG_GAME_SOCKET) console.log(`Player ${leftPlayerId} has left the table.`);
      }
    });


    socket.on('disconnect', () => {
      if (DEBUG_GAME_SOCKET) console.log('Disconnected from game server');
      if (activeRoundEndToastId !== null) {
        toast.dismiss(activeRoundEndToastId);
        activeRoundEndToastId = null;
      }
      set({ isConnected: false });
    });

    presenceInterval = setInterval(() => {
      socket.emit('presenceHeartbeat', { userId });
    }, 20000);

    set({ socket });
  },

  disconnect: () => {
    const { socket } = get();
    if (socket) {
      socket.disconnect();
      set({ socket: null, gameState: null, tableInfo: null, lastRoundResult: null, isConnected: false });
    }
    if (presenceInterval) {
      clearInterval(presenceInterval);
      presenceInterval = null;
    }
  },

  leaveTable: (tableId, userId, username) => {
    const { socket } = get();
    if (socket) {
      socket.emit('leaveTable', { tableId, userId, username });
      // We don't disconnect here, we let the component handle navigation which will trigger cleanup
      // Or we can explicitly disconnect if we want to be sure
      set({ gameState: null, tableInfo: null });
    }
  },

  drawCard: (tableId, userId, source) => {
    const { socket } = get();
    if (socket) socket.emit('drawCard', { tableId, userId, source });
  },

  discardCard: (tableId, userId, card) => {
    const { socket } = get();
    if (socket) socket.emit('discardCard', { tableId, userId, card });
  },

  spread: (tableId, userId, cards) => {
    const { socket } = get();
    if (socket) socket.emit('spread', { tableId, userId, cards });
  },

  hit: (tableId, userId, card, targetPlayerId, targetSpreadIndex) => {
    const { socket } = get();
    if (socket) socket.emit('hit', { tableId, userId, card, targetPlayerId, targetSpreadIndex });
  },

  drop: (tableId, userId) => {
    const { socket } = get();
    if (socket) socket.emit('drop', { tableId, userId });
  },

  declare41: (tableId, userId) => {
    const { socket } = get();
    if (socket) socket.emit('declare41', { tableId, userId });
  },

  reorderHand: (tableId, userId, handOrder) => {
    const { socket } = get();
    if (socket) socket.emit('reorderHand', { tableId, userId, handOrder });
  },

  requestLeaveTable: (tableId, userId) => {
    const { socket } = get();
    if (socket) socket.emit('requestLeaveTable', { tableId, userId });
  },

  putIn: (tableId, userId) => {
    const { socket } = get();
    if (socket) socket.emit('putIn', { tableId, userId });
  },
}));
